import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Appointment} from '../models/appointment.model';
import {Injectable} from '@angular/core';
import {Medication} from '../models/medication.model';
import {SickNote} from '../models/sick-note.model';
import {AppointmentStatus} from '../enums/appointment-status.enum';
import {BookingSlot} from '../models/booking-slot.model';
import {DoctorReportSummary} from '../models/doctor-report-summary';
import { EmergencyContact } from '../models/emergency-contact.model';
import { environment } from '../../environments/environment';
import { WalkInBookingRequest } from '../models/walk-in-booking-request.model';
import { Patient } from '../models/patient.model';
import { ConsultationPricingRequest } from '../models/consultation-pricing-request.model';
import { AppointmentInvoiceDto } from '../models/appointment-invoice.model';
import { SaveInvoiceSettlementsRequest } from '../models/save-invoice-settlements-request.model';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private apiUrl = environment.APP_BASE_URL + '/doctor-portal/booking-view/appointment'; // replace with real API endpoint
  
  constructor(private http: HttpClient) {}

  confirmBooking(appointment: Appointment | null | undefined): Observable<Appointment> {
    return this.http.put<Appointment>(`${this.apiUrl}/confirm`, appointment);
  }

  completeBooking(appointment: Appointment | null | undefined): Observable<Appointment> {
    return this.http.put<Appointment>(`${this.apiUrl}/complete`, appointment);
  }

  markNoShow(appointment: Appointment | null | undefined): Observable<Appointment> {
    return this.http.put<Appointment>(`${this.apiUrl}/no-show`, appointment);
  }

  cancelBooking(appointment: Appointment | null | undefined): Observable<Appointment> {
    return this.http.put<Appointment>(`${this.apiUrl}/cancel`, appointment);
  }

  savePrescriptionMedication(medications: Medication[], appointmentId: string | undefined) {
    return this.http.post<string>(`${this.apiUrl}/prescription`, {
      medications,
      appointmentId: appointmentId,
    });
  }

  saveDoctorNotes(appointment: Appointment | null | undefined): Observable<Appointment> {
    return this.http.patch<Appointment>(`${this.apiUrl}/${appointment?.id}/doctor-notes`, {
      doctorNotes: appointment?.doctorNotes ?? ''
    });
  }

  writeSickNote(sickNote: SickNote | undefined) : Observable<SickNote> {
    return this.http.post<SickNote>(`${this.apiUrl}/sick-note`, sickNote);
  }

  generateConsultationPricingDocument(request: ConsultationPricingRequest): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/consultation-invoice`, request, {
      responseType: 'blob'
    });
  }

  getAppointmentInvoice(appointmentId: string): Observable<AppointmentInvoiceDto | null> {
    return this.http.get<AppointmentInvoiceDto | null>(`${this.apiUrl}/${appointmentId}/invoice`);
  }

  saveInvoiceSettlements(request: SaveInvoiceSettlementsRequest): Observable<AppointmentInvoiceDto> {
    return this.http.post<AppointmentInvoiceDto>(`${this.apiUrl}/${request.appointmentId}/invoice/settlements`, request);
  }

  getPatientBookings(): Observable<Appointment[]> {
    return this.http.get<Appointment[]>(`${this.apiUrl}/all`);
  }

  getPatientBooking(id: string | undefined): Observable<Appointment> {
    return this.http.get<Appointment>(`${this.apiUrl}/booking/${id}`);
  }

  getPatientBookingsByStatus(status: AppointmentStatus): Observable<Appointment[]> {
    return this.http.get<Appointment[]>(`${this.apiUrl}/all/status/${status.toString()}`);
  }

  getDailySchedule(doctorId?: string | null): Observable<BookingSlot[]> {
    const params: Record<string, string> = {};
    if (doctorId) {
      params['doctorId'] = doctorId;
    }
    return this.http.get<BookingSlot[]>(`${this.apiUrl}/slots`, { params });
  }

  getSlotsForDoctorAndDate(doctorId: string | null | undefined, date: string): Observable<BookingSlot[]> {
    const params: Record<string, string> = {};
    if (doctorId) {
      params['doctorId'] = doctorId;
    }
    params['date'] = date;
    return this.http.get<BookingSlot[]>(`${this.apiUrl}/slots`, { params });
  }

  getReportSummary(doctorId: string | null | undefined): Observable<DoctorReportSummary> {
    const params: Record<string, string> = {};
    if (doctorId) {
      params['doctorId'] = doctorId;
    }
    return this.http.get<DoctorReportSummary>(`${this.apiUrl}/report`, { params });
  }

  startBooking(bookingsId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/start/${bookingsId}`);
  }

  createWalkInBooking(request: WalkInBookingRequest): Observable<Appointment> {
    return this.http.post<Appointment>(`${this.apiUrl}/walk-in`, request);
  }

  deleteDocument(appointmentId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/documents/${appointmentId}/${documentId}`);
  }

  uploadBookingDocuments(formData: FormData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/documents`, formData);
  }



}
